import * as React from "react";
import { useEffect, useRef } from "react";
import { Control, UseFormRegister, UseFormWatch, UseFormSetValue, UseFormTrigger, FieldErrors } from "react-hook-form";
import styles from "./styles.module.scss";

interface RankingItem {
  id: string;
  text: string;
  rank: any;
  value?: string;
}

interface VerticalRankingFieldProps {
  control: Control<any>;
  register: UseFormRegister<any>;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  trigger: UseFormTrigger<any>;
  errors: FieldErrors<any>;
  name: string;
  questionText: string;
  questionNumber: number;
  subtitle: string;
  containerClassName?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  items: RankingItem[];
}

// 1st = green, 2nd = indigo, 3rd = amber, 4th = red (cycles for longer lists).
const RANK_COLORS = ['#16a34a', '#4f46e5', '#d97706', '#dc2626'];

const getRankColor = (rank: number): string => RANK_COLORS[(rank - 1) % RANK_COLORS.length];

const ordinal = (n: number): string => {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]}`;
};

const rankLabel = (rank: number): string => `${ordinal(rank)} choice`;

interface RankSelectProps {
  rank: number | null;
  // Ranks available to pick for this row (already-taken ranks are excluded).
  availableRanks: number[];
  onSelect: (rank: number) => void;
  onClear: () => void;
}

const RankSelect: React.FC<RankSelectProps> = ({ rank, availableRanks, onSelect, onClear }) => {
  const [open, setOpen] = React.useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className={styles.rankSelect} ref={ref}>
      <button
        type="button"
        className={`${styles.rankTrigger} ${rank ? styles.rankTriggerSelected : ''}`}
        style={rank ? { color: getRankColor(rank) } : undefined}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{rank ? rankLabel(rank) : 'Select'}</span>
        <svg className={styles.chevron} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className={styles.rankMenu} role="listbox">
          {availableRanks.map((r) => (
            <button
              key={r}
              type="button"
              role="option"
              className={styles.rankOption}
              style={{ color: getRankColor(r) }}
              onClick={() => {
                onSelect(r);
                setOpen(false);
              }}
            >
              {rankLabel(r)}
            </button>
          ))}
          {rank !== null && (
            <button
              type="button"
              className={`${styles.rankOption} ${styles.clearOption}`}
              onClick={() => {
                onClear();
                setOpen(false);
              }}
            >
              Clear ranking
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const VerticalRankingField: React.FC<VerticalRankingFieldProps> = ({
  register,
  watch,
  setValue,
  trigger,
  errors,
  name,
  questionText,
  questionNumber,
  subtitle,
  containerClassName,
  titleClassName,
  subtitleClassName,
  items: initialItems,
}) => {
  const watchedValue = watch(name);
  const [isInitialized, setIsInitialized] = React.useState(false);

  const options = React.useMemo(
    () => initialItems.filter((item) => item != null),
    [initialItems]
  );

  // Map of option id -> assigned rank. Empty by default so an untouched question
  // is never submitted as "answered" with a phantom default order.
  const [ranks, setRanks] = React.useState<Record<string, number>>({});

  // Hydrate from a previously saved value (e.g. resuming an assessment), without
  // writing any default ranking back to the form on mount.
  useEffect(() => {
    if (isInitialized) return;
    setIsInitialized(true);
    if (watchedValue && Array.isArray(watchedValue) && watchedValue.length > 0) {
      const hydrated: Record<string, number> = {};
      watchedValue
        .filter((item: any) => item != null && item.id != null)
        .forEach((item: any, index: number) => {
          const rank = Number(item.rank ?? index + 1);
          if (!Number.isNaN(rank)) hydrated[item.id] = rank;
        });
      setRanks(hydrated);
    }
  }, [watchedValue, isInitialized]);

  // Sync the form value: an array of ranked items ordered by rank (index 0 = 1st
  // choice), matching the submission contract. Empty when nothing is ranked.
  const syncFormValue = (nextRanks: Record<string, number>) => {
    const ordered = options
      .filter((item) => nextRanks[item.id] != null)
      .sort((a, b) => nextRanks[a.id] - nextRanks[b.id])
      .map((item) => ({
        id: item.id,
        text: item.text,
        value: item.value,
        rank: nextRanks[item.id],
      }));
    setValue(name, ordered, { shouldDirty: true });
    trigger(name);
  };

  const handleSelect = (itemId: string, rank: number) => {
    const next = { ...ranks };
    // A rank is unique across options; drop it from any row that already held it.
    Object.keys(next).forEach((key) => {
      if (next[key] === rank) delete next[key];
    });
    next[itemId] = rank;
    setRanks(next);
    syncFormValue(next);
  };

  const handleClear = (itemId: string) => {
    const next = { ...ranks };
    delete next[itemId];
    setRanks(next);
    syncFormValue(next);
  };

  const getErrorMessage = () => {
    const error = errors[name];
    if (error && typeof error.message === 'string') {
      return error.message;
    }
    return '';
  };

  const takenRanks = new Set(Object.values(ranks));
  const allRanks = Array.from({ length: options.length }, (_, i) => i + 1);

  return (
    <div className={`${styles.container} ${containerClassName || ''}`}>
      <div className={styles.questionHeader}>
        <h3 className={`${styles.questionTitle} ${titleClassName || ''}`}>
          {questionNumber}. {questionText}
        </h3>
        <p className={`${styles.subtitle} ${subtitleClassName || ''}`}>
          {subtitle}
        </p>
      </div>

      <div className={styles.rankingTable}>
        <div className={styles.tableHeader}>
          <span className={styles.rankingColumnLabel}>#Ranking</span>
          <span className={styles.optionsColumnLabel}>Available Options</span>
        </div>

        {options.map((item) => {
          const currentRank = ranks[item.id] ?? null;
          // Offer ranks not already used elsewhere; the row keeps "Clear ranking"
          // (rendered by RankSelect) to un-rank its current choice.
          const availableRanks = allRanks.filter((r) => !takenRanks.has(r));

          return (
            <div key={item.id} className={styles.tableRow}>
              <div className={styles.rankingCell}>
                <RankSelect
                  rank={currentRank}
                  availableRanks={availableRanks}
                  onSelect={(rank) => handleSelect(item.id, rank)}
                  onClear={() => handleClear(item.id)}
                />
              </div>
              <div className={styles.optionCell}>
                <span className={styles.itemText}>{item.text}</span>
              </div>
            </div>
          );
        })}
      </div>

      {getErrorMessage() && (
        <div className={styles.errorMessage}>
          {getErrorMessage()}
        </div>
      )}

      {/* Hidden input for form registration */}
      <input
        type="hidden"
        {...register(name)}
        value={JSON.stringify(options.filter((item) => ranks[item.id] != null))}
      />
    </div>
  );
};

export default VerticalRankingField;
